import { useReportBuilderStore } from '../../stores/reportBuilderStore';
import { Switch } from '@t-one-internal/design-system';
import {
  ChartBar,
  ChartLine,
  ChartPie,
  ChartDonut,
  ChartScatter,
  ChartPolar,
  Rows,
  SquaresFour,
  Stack,
} from '@phosphor-icons/react';
import type { ReportChartType } from '../../types/reportBuilder';

const CHART_TYPES: { type: ReportChartType; label: string; icon: typeof ChartBar }[] = [
  { type: 'column', label: 'Bar', icon: ChartBar },
  { type: 'h-bar', label: 'H-Bar', icon: Rows },
  { type: 'stacked', label: 'Stacked', icon: Stack },
  { type: 'line', label: 'Line', icon: ChartLine },
  { type: 'area', label: 'Area', icon: SquaresFour },
  { type: 'pie', label: 'Pie', icon: ChartPie },
  { type: 'donut', label: 'Donut', icon: ChartDonut },
  { type: 'radar', label: 'Radar', icon: ChartPolar },
  { type: 'scatter', label: 'Scatter', icon: ChartScatter },
];


export default function ChartConfigPanel() {
  const {
    results, chartType, chartFieldMapping, showLegend,
    setChartType, setChartFieldMapping, setShowLegend,
  } = useReportBuilderStore();

  if (!results) return null;

  const dimensionCols = results.columns.filter(c => c.is_dimension);
  const measureCols = results.columns.filter(c => c.is_measure);

  return (
    <div className="space-y-5">
      {/* Chart Type Grid */}
      <div>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Chart Type</h3>
        <div className="grid grid-cols-3 gap-1.5">
          {CHART_TYPES.map(({ type, label, icon: Icon }) => (
            <button
              key={type}
              onClick={() => setChartType(type)}
              className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-2 text-xs font-medium transition-colors ${
                chartType === type
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Icon size={18} weight={chartType === type ? 'fill' : 'regular'} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Field Mapping */}
      {chartFieldMapping && (
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Fields</h3>
          <div className="space-y-2">
            <div>
              <label className="mb-1 block text-[11px] font-medium text-gray-500">X-Axis</label>
              <select
                value={chartFieldMapping.nameField}
                onChange={(e) => setChartFieldMapping({ ...chartFieldMapping, nameField: e.target.value })}
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none"
              >
                {dimensionCols.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-medium text-gray-500">Y-Axis</label>
              <select
                value={chartFieldMapping.valueField}
                onChange={(e) => setChartFieldMapping({ ...chartFieldMapping, valueField: e.target.value })}
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none"
              >
                {measureCols.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-medium text-gray-500">Series / Color</label>
              <select
                value={chartFieldMapping.seriesField ?? ''}
                onChange={(e) => setChartFieldMapping({ ...chartFieldMapping, seriesField: e.target.value || undefined })}
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none"
              >
                <option value="">None</option>
                {dimensionCols
                  .filter(c => c.key !== chartFieldMapping.nameField)
                  .map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Display Options */}
      <div>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Display</h3>
        <div className="space-y-2.5">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-700">Legend</span>
            <Switch checked={showLegend} onCheckedChange={setShowLegend} />
          </div>
        </div>
      </div>
    </div>
  );
}
